import { useCallback, useMemo, useState } from 'react';
import { useInstanciasDisponibles } from '@/shared/hooks/useBuzonJudicial.hook';
import type {
  BuzonActivationResult,
  BuzonCategoriaTribunal,
  BuzonDetalleCasoInstancias,
  BuzonNucPreference,
} from '@/shared/types/interfaces/BuzonJudicial.interface';

export type ModoHabilitacion = 'NUC' | 'INSTANCIA';

/**
 * 'estandar' = primera habilitación (individual o batch, casos NO_HABILITADO/DESHABILITADO/RECHAZADO).
 * 'nueva' = caso ya HABILITADO por instancia al que se le agrega una instancia nueva disponible (punto 11).
 */
export type OrigenHabilitacion = 'estandar' | 'nueva';

export interface GrupoActivacionInstancia {
  idsCategoriaTribunal: number[];
  idsCasos: number[];
}

export interface ResumenActivacionAgrupada {
  modo: ModoHabilitacion;
  exitosos: Array<{ idsCasos: number[]; idsCategoriaTribunal?: number[]; result: BuzonActivationResult }>;
  fallidos: Array<{ idsCasos: number[]; idsCategoriaTribunal?: number[]; error: unknown }>;
}

interface HabilitarBuzonJudicialDeps {
  activateNuc: (idCaso: number) => Promise<BuzonActivationResult>;
  activateSelected: (idsCasos: number[]) => Promise<BuzonActivationResult>;
  activateConInstancia: (idsCasos: number[], idsCategoriaTribunal: number[]) => Promise<BuzonActivationResult>;
  invalidateBuzonQueries: () => void;
}

/**
 * Invierte una selección caso→categorías en grupos (mismo conjunto de categorías)→casos[], para
 * respetar que el backend solo acepta UN idsCategoriaTribunal compartido por cada llamada
 * POST /solicitud-activar. Casos con el MISMO conjunto de categorías seleccionadas (sin importar el
 * orden) se agrupan en una sola llamada; casos con conjuntos distintos generan llamadas separadas.
 */
export function agruparCasosPorInstancia(seleccion: Map<number, number[]>): GrupoActivacionInstancia[] {
  const porFirma = new Map<string, GrupoActivacionInstancia>();
  for (const [idCaso, idsCategoriaTribunal] of seleccion) {
    if (idsCategoriaTribunal.length === 0) continue;
    const ordenado = [...idsCategoriaTribunal].sort((a, b) => a - b);
    const firma = ordenado.join(',');
    const grupo = porFirma.get(firma);
    if (grupo) {
      grupo.idsCasos.push(idCaso);
    } else {
      porFirma.set(firma, { idsCategoriaTribunal: ordenado, idsCasos: [idCaso] });
    }
  }
  return Array.from(porFirma.values());
}

/**
 * Categorías disponibles para un caso puntual, resueltas contra el catálogo de instancias disponibles.
 * Compara `idCaso` como string: GET /api/buzon/nucs lo devuelve como string (ej. "4239757") mientras
 * que GET /api/buzon/instancias-disponibles lo devuelve como number (ej. 4239757) — sin esta coerción
 * la comparación estricta nunca encuentra coincidencias y el selector queda siempre sin opciones.
 */
export function filtrarCategoriasPorCaso(
  idCaso: number,
  detallePorCaso: BuzonDetalleCasoInstancias[],
  instanciasDisponibles: BuzonCategoriaTribunal[],
): BuzonCategoriaTribunal[] {
  const idsDisponibles = detallePorCaso.find((d) => String(d.idCaso) === String(idCaso))?.categoriasDisponibles ?? [];
  return instanciasDisponibles.filter((cat) => idsDisponibles.includes(cat.idCategoriaTribunal));
}

/** Categorías disponibles para un caso, EXCLUYENDO las que ya están autorizadas (punto 11: "instancia nueva"). */
export function filtrarCategoriasNuevasParaCaso(
  row: BuzonNucPreference,
  detallePorCaso: BuzonDetalleCasoInstancias[],
  instanciasDisponibles: BuzonCategoriaTribunal[],
): BuzonCategoriaTribunal[] {
  const autorizadas = new Set((row.categoriasAutorizadas ?? []).map((c) => c.idCategoriaTribunal));
  return filtrarCategoriasPorCaso(row.idCaso, detallePorCaso, instanciasDisponibles).filter(
    (cat) => !autorizadas.has(cat.idCategoriaTribunal),
  );
}

/**
 * Hook "cerebro" del flujo de habilitación del Buzón Judicial (modal "Habilitar por:").
 * No conoce sileo ni i18next: retorna un resumen plano para que BuzonJudicial.tsx componga los toasts,
 * igual que ya hace hoy con showAuthorizationEmailSent.
 */
export const useHabilitarBuzonJudicial = (deps: HabilitarBuzonJudicialDeps) => {
  const { activateNuc, activateSelected, activateConInstancia, invalidateBuzonQueries } = deps;

  const [open, setOpen] = useState(false);
  const [casos, setCasos] = useState<BuzonNucPreference[]>([]);
  const [origen, setOrigen] = useState<OrigenHabilitacion>('estandar');
  const [modo, setModo] = useState<ModoHabilitacion>('NUC');
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState<BuzonCategoriaTribunal[]>([]);
  const [seleccionPorCaso, setSeleccionPorCaso] = useState<Map<number, BuzonCategoriaTribunal[]>>(new Map());
  const [isConfirming, setIsConfirming] = useState(false);

  const idsCasos = useMemo(() => casos.map((c) => c.idCaso), [casos]);
  const esMultiple = casos.length > 1;
  const requiereInstancias = open && (modo === 'INSTANCIA' || origen === 'nueva');

  const {
    data: instanciasData,
    isLoading: isLoadingInstancias,
    isError: isErrorInstancias,
    refetch: refetchInstancias,
  } = useInstanciasDisponibles(idsCasos, requiereInstancias);

  const instanciasDisponibles = instanciasData?.instanciasDisponibles ?? [];
  const detallePorCaso = instanciasData?.detallePorCaso ?? [];

  /** Abre el modal. `origen: 'nueva'` es siempre para 1 solo caso (punto 11, botón individual de fila). */
  const abrirModal = useCallback((casosObjetivo: BuzonNucPreference[], origenModal: OrigenHabilitacion = 'estandar') => {
    setCasos(casosObjetivo);
    setOrigen(origenModal);
    setModo(origenModal === 'nueva' ? 'INSTANCIA' : 'NUC');
    setCategoriasSeleccionadas([]);
    setSeleccionPorCaso(new Map());
    setOpen(true);
  }, []);

  const cerrarModal = useCallback(() => {
    setOpen(false);
  }, []);

  const setCategoriaParaCaso = useCallback((idCaso: number, categorias: BuzonCategoriaTribunal[]) => {
    setSeleccionPorCaso((prev) => {
      const next = new Map(prev);
      if (categorias.length > 0) {
        next.set(idCaso, categorias);
      } else {
        next.delete(idCaso);
      }
      return next;
    });
  }, []);

  const canConfirmar = useMemo(() => {
    if (modo === 'NUC') return true;
    if (isLoadingInstancias || isErrorInstancias) return false;
    if (origen === 'nueva' || !esMultiple) return categoriasSeleccionadas.length > 0;
    return casos.length > 0 && casos.every((c) => (seleccionPorCaso.get(c.idCaso)?.length ?? 0) > 0);
  }, [modo, isLoadingInstancias, isErrorInstancias, origen, esMultiple, categoriasSeleccionadas, casos, seleccionPorCaso]);

  const confirmar = useCallback(async (): Promise<ResumenActivacionAgrupada> => {
    setIsConfirming(true);
    try {
      if (modo === 'NUC') {
        try {
          const result = casos.length === 1
            ? await activateNuc(casos[0].idCaso)
            : await activateSelected(idsCasos);
          invalidateBuzonQueries();
          return { modo, exitosos: [{ idsCasos, result }], fallidos: [] };
        } catch (error) {
          return { modo, exitosos: [], fallidos: [{ idsCasos, error }] };
        }
      }

      // modo === 'INSTANCIA'
      const seleccionEfectiva = new Map<number, number[]>();
      if (origen === 'nueva' || !esMultiple) {
        seleccionEfectiva.set(casos[0].idCaso, categoriasSeleccionadas.map((c) => c.idCategoriaTribunal));
      } else {
        for (const caso of casos) {
          const categorias = seleccionPorCaso.get(caso.idCaso) ?? [];
          seleccionEfectiva.set(caso.idCaso, categorias.map((c) => c.idCategoriaTribunal));
        }
      }

      const grupos = agruparCasosPorInstancia(seleccionEfectiva);
      const settled = await Promise.allSettled(
        grupos.map((grupo) =>
          activateConInstancia(grupo.idsCasos, grupo.idsCategoriaTribunal).then((result) => ({ ...grupo, result })),
        ),
      );

      const resumen: ResumenActivacionAgrupada = { modo, exitosos: [], fallidos: [] };
      settled.forEach((settledResult, index) => {
        const grupo = grupos[index];
        if (settledResult.status === 'fulfilled') {
          resumen.exitosos.push({ idsCasos: grupo.idsCasos, idsCategoriaTribunal: grupo.idsCategoriaTribunal, result: settledResult.value.result });
        } else {
          resumen.fallidos.push({ idsCasos: grupo.idsCasos, idsCategoriaTribunal: grupo.idsCategoriaTribunal, error: settledResult.reason });
        }
      });

      if (resumen.exitosos.length > 0) {
        invalidateBuzonQueries();
      }
      return resumen;
    } finally {
      setIsConfirming(false);
    }
  }, [modo, casos, idsCasos, origen, esMultiple, categoriasSeleccionadas, seleccionPorCaso, activateNuc, activateSelected, activateConInstancia, invalidateBuzonQueries]);

  return {
    isOpen: open,
    casos,
    origen,
    modo,
    esMultiple,
    categoriasSeleccionadas,
    seleccionPorCaso,
    instanciasDisponibles,
    detallePorCaso,
    isLoadingInstancias,
    isErrorInstancias,
    refetchInstancias,
    canConfirmar,
    isConfirming,
    abrirModal,
    cerrarModal,
    setModo,
    setCategoriasSeleccionadas,
    setCategoriaParaCaso,
    confirmar,
    filtrarCategoriasPorCaso: (idCaso: number) => filtrarCategoriasPorCaso(idCaso, detallePorCaso, instanciasDisponibles),
    filtrarCategoriasNuevasParaCaso: (row: BuzonNucPreference) => filtrarCategoriasNuevasParaCaso(row, detallePorCaso, instanciasDisponibles),
  };
};
